import { inject, Service } from "@angular/core";
import { filter, switchMap, take, timer } from "rxjs";

import { environment } from "../../core/environments/environment";
import { SynchronizationScheduleEndPoints } from "../constants/enums";
import {
  ClientType,
  ConfiguredScheduler,
  EntityDataManualTriggerForm,
  EntityDataSyncDataTypes,
  EntityModelType,
  JobSchedule,
  MainResponseModel,
  RunningJob,
  WfCleanupConfig,
} from "../models";
import { MainService } from ".";

@Service()
export class SyncApiService {
  private _mainService = inject(MainService);

  public getRunningResponse(endpoint: SynchronizationScheduleEndPoints) {
    const url = `${endpoint}/getRunningResponse`;

    return this._mainService.sendRequest<
      MainResponseModel<{
        lastDate: number;
        isRunning: boolean;
      }>
    >({
      method: "GET",
      apiUrl: endpoint === SynchronizationScheduleEndPoints.jobs ? `${environment.khadamatiApiUrl}${url}` : undefined,
      url: endpoint !== SynchronizationScheduleEndPoints.jobs ? url : "",
      loader: false,
    });
  }

  public getCurrentScheduler(endpoint: SynchronizationScheduleEndPoints) {
    const url = `${endpoint}/getCurrentScheduler`;

    return this._mainService.sendRequest<MainResponseModel<JobSchedule>>({
      method: "GET",
      apiUrl: endpoint === SynchronizationScheduleEndPoints.jobs ? `${environment.khadamatiApiUrl}${url}` : undefined,
      url: endpoint !== SynchronizationScheduleEndPoints.jobs ? url : "",
    });
  }

  public configureScheduler(endpoint: SynchronizationScheduleEndPoints, data: { cron: string }) {
    const url = `${endpoint}/configureScheduler`;

    return this._mainService.sendRequest<MainResponseModel<undefined>>({
      method: "POST",
      apiUrl: endpoint === SynchronizationScheduleEndPoints.jobs ? `${environment.khadamatiApiUrl}${url}` : undefined,
      url: endpoint !== SynchronizationScheduleEndPoints.jobs ? url : "",
      data,
    });
  }

  public disableScheduler(endpoint: SynchronizationScheduleEndPoints) {
    const url = `${endpoint}/disableScheduler`;

    return this._mainService.sendRequest<MainResponseModel<undefined>>({
      method: "POST",
      apiUrl: endpoint === SynchronizationScheduleEndPoints.jobs ? `${environment.khadamatiApiUrl}${url}` : undefined,
      url: endpoint !== SynchronizationScheduleEndPoints.jobs ? url : "",
      data: {},
    });
  }

  public stopSync(endpoint: SynchronizationScheduleEndPoints, jobId?: number) {
    const url = jobId ? `${endpoint}/stopSync/${jobId}` : `${endpoint}/stopSync`;

    return this._mainService.sendRequest<MainResponseModel<undefined>>({
      method: "POST",
      apiUrl: endpoint === SynchronizationScheduleEndPoints.jobs ? `${environment.khadamatiApiUrl}${url}` : undefined,
      url: endpoint !== SynchronizationScheduleEndPoints.jobs ? url : "",
      data: {},
    });
  }

  public manualTrigger(endpoint: SynchronizationScheduleEndPoints, data?: EntityDataManualTriggerForm) {
    const url = `${endpoint}/manualTrigger`;

    return this._mainService.sendRequest<MainResponseModel<number | undefined>>({
      method: "POST",
      apiUrl: endpoint === SynchronizationScheduleEndPoints.jobs ? `${environment.khadamatiApiUrl}${url}` : undefined,
      url: endpoint !== SynchronizationScheduleEndPoints.jobs ? url : "",
      data: data ?? {},
    });
  }

  public getSyncStatus(endpoint: SynchronizationScheduleEndPoints, multiplier: number) {
    return timer(1000, 2000 + multiplier * 1000).pipe(
      switchMap(() => this.getRunningResponse(endpoint)),
      filter(
        (
          response: MainResponseModel<{
            lastDate: number;
            isRunning: boolean;
          }>
        ) => response.responseData.isRunning === false
      ),
      take(1)
    );
  }

  public getDataTypes(clientType?: number) {
    const url = clientType ? `entity-data-sync/getDataTypes?clientType=${clientType}` : "entity-data-sync/getDataTypes";

    return this._mainService.sendRequest<MainResponseModel<EntityDataSyncDataTypes[]>>({
      method: "GET",
      url,
    });
  }

  public getWfCleanupConfig() {
    return this._mainService.sendRequest<MainResponseModel<WfCleanupConfig>>({
      method: "GET",
      url: "wf-clean-up-config",
    });
  }

  public updateWfCleanupConfig(data: WfCleanupConfig) {
    return this._mainService.sendRequest<MainResponseModel<WfCleanupConfig>>({
      method: "PUT",
      url: "wf-clean-up-config",
      data,
    });
  }

  public getKhadamatiLastSyncDate() {
    const url = `khadamati/getLastDate`;

    return this._mainService.sendRequest<MainResponseModel<{ lastDate: number }>>({
      method: "GET",
      url,
      loader: false,
    });
  }

  public getScheduler(id: number) {
    return this._mainService.sendRequest<MainResponseModel<ConfiguredScheduler>>({
      method: "GET",
      url: `entity-data-sync/getCurrentScheduler/${id}`,
    });
  }

  public getEntities() {
    return this._mainService.sendRequest<MainResponseModel<EntityModelType[]>>({
      method: "GET",
      url: "entity-data-sync/getEntities",
    });
  }

  public getClientTypes(entityId: number) {
    return this._mainService.sendRequest<MainResponseModel<ClientType[]>>({
      method: "GET",
      url: `entity-data-sync/getClientTypes?entityId=${entityId}`,
    });
  }

  public saveConfiguredScheduler(data: ConfiguredScheduler) {
    return this._mainService.sendRequest<MainResponseModel<ConfiguredScheduler>>({
      method: "POST",
      url: "entity-data-sync/configureScheduler",
      data,
    });
  }

  public disableSchedulerById(id: number) {
    return this._mainService.sendRequest<MainResponseModel>({
      method: "POST",
      url: `entity-data-sync/disableScheduler/${id}`,
      data: {},
    });
  }

  public manualTriggerEntityData(data?: EntityDataManualTriggerForm) {
    return this._mainService.sendRequest<MainResponseModel<RunningJob[]>>({
      method: "POST",
      url: `entity-data-sync/manualTrigger`,
      data: data ?? {},
    });
  }

  public getRunningResponseByJobId(jobId: number) {
    return this._mainService.sendRequest<MainResponseModel<RunningJob[]>>({
      method: "POST",
      url: `entity-data-sync/getRunningResponses`,
      loader: false,
      data: {
        logIds: [jobId],
      },
    });
  }
}
